import React from 'react';
import { defaultTo, get } from 'lodash';
import {
  BillingFrequency,
  RelationshipPathType,
  FetchEntitlementDetailsForAppLinksPpcQuery,
  FetchEntitlementDetailsForAppLinksPpcQueryVariables,
  FetchEntitlementsForFamilyContainerPpcQuery,
  FetchEntitlementsForFamilyContainerPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { isJiraProduct } from '@atlassian/cssxp-utils-product';
import { abgGraphqlClient as graphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { fetchEntitlementDetailsForAppLinksPPCQuery } from '../queries/fetch-entitlement-details-for-app-links';
import { fetchEntitlementsForFamilyContainerPPC } from '../queries/fetch-entitlements-for-family-container';
import { checkAppCompatibilityWithParentProductQuery } from '../queries/sxp-queries/fetch-parent-product-relationship-with-app';
import { CheckoutOperation, usePartnerCheckoutNotification } from './use-place-order-notification';
import { ParentEntitlementDetails } from '../types';

const JIRA_RELATIONSHIP_CONTAINER_ID = '5a1a172d-651c-4c76-a5ec-70543564372c';

interface UseParentProductToAppHookType {
  txaId: string;
  appProductKey: string;
}
interface UseParentProductToAppHookResponseType {
  searchEntitlement: (searchKey: string) => Promise<void>;
  isNotParentProductCompatible: boolean;
  isEnabledEntitlementChangeButton: boolean;
  setIsNotParentProductCompatible: (param: boolean) => void;
  setEnabledEntitlementChangeButton: (param: boolean) => void;
  data: ParentEntitlementDetails | undefined;
  loading: boolean;
  error: Error | null;
  entitlementValidationError: string | null;
  isMaxUsersDetailLoading: boolean;
  setEntitlementDetails: (param: ParentEntitlementDetails | undefined) => void;
  setIsReadyToFetchMaxUsersDetail: (param: boolean) => void;
}

export const useLinkParentProductToApp = ({
  txaId,
  appProductKey,
}: UseParentProductToAppHookType): UseParentProductToAppHookResponseType => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isNotParentProductCompatible, setIsNotParentProductCompatible] = React.useState<boolean>(false);
  const [isReadyToFetchMaxUsersDetail, setIsReadyToFetchMaxUsersDetail] = React.useState<boolean>(false);
  const [isMaxUsersDetailLoading, setIsMaxUsersDetailLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [entitlementValidationError, setEntitlementValidationError] = React.useState<string | null>(null);
  const [entitlementDetails, setEntitlementDetails] = React.useState<ParentEntitlementDetails | undefined>(undefined);
  const [isEnabledEntitlementChangeButton, setEnabledEntitlementChangeButton] = React.useState<boolean>(false);

  const { notifyFailure } = usePartnerCheckoutNotification();

  React.useEffect(() => {
    (async () => {
      if (isReadyToFetchMaxUsersDetail && entitlementDetails) {
        setIsMaxUsersDetailLoading(true);
        try {
          const relatesFromEntitlementId =
            entitlementDetails?.familyContainerId ||
            defaultTo(get(entitlementDetails, 'relatesFromEntitlements[0].entitlementId'), '');
          if (relatesFromEntitlementId) {
            const entitlementResponse = await graphqlClient(
              ProxyGraphClients.ANS
            ).query<FetchEntitlementsForFamilyContainerPpcQuery>({
              query: fetchEntitlementsForFamilyContainerPPC,
              variables: {
                id: txaId,
                filterOptions: [
                  {
                    key: 'relatesFromEntitlementsRelationshipType',
                    value: RelationshipPathType.FAMILY_CONTAINER,
                  },
                  {
                    key: 'relatesFromEntitlementsEntitlementId',
                    value: relatesFromEntitlementId,
                  },
                ],
              } as FetchEntitlementsForFamilyContainerPpcQueryVariables,
              fetchPolicy: 'no-cache',
            });
            const allFamilyContainerEntitlements = defaultTo(
              get(entitlementResponse, 'data.transactionAccount.entitlements.nodes'),
              []
            );
            const maxUserEntitlement = allFamilyContainerEntitlements?.reduce(function (prev: any, current: any) {
              return prev?.unitCount > current?.unitCount ? prev : current;
            });
            setIsReadyToFetchMaxUsersDetail(false);
            setIsMaxUsersDetailLoading(false);
            setEntitlementDetails({ ...entitlementDetails, maxUnitCount: String(maxUserEntitlement?.unitCount) });
          }
        } catch (error) {
          setError(error as Error);
          notifyFailure(
            (error as Error)?.message ??
              'Something is not right at our end. Please try again. If the issue persists, please contact us.',
            CheckoutOperation.MaxUsersForFamilyContainer
          );
        } finally {
          setIsReadyToFetchMaxUsersDetail(false);
          setIsMaxUsersDetailLoading(false);
        }
      }
    })();
  }, [appProductKey, isReadyToFetchMaxUsersDetail, entitlementDetails]);

  const searchEntitlement = async (searchKey: string) => {
    setLoading(true);
    setIsReadyToFetchMaxUsersDetail(false);
    setEntitlementValidationError(null);
    try {
      const entitlementResponse = await graphqlClient(
        ProxyGraphClients.ANS
      ).query<FetchEntitlementDetailsForAppLinksPpcQuery>({
        query: fetchEntitlementDetailsForAppLinksPPCQuery,
        variables: {
          id: searchKey?.trim(),
          transactionAccountId: txaId,
        } as FetchEntitlementDetailsForAppLinksPpcQueryVariables,
        fetchPolicy: 'no-cache',
      });

      if (entitlementResponse?.errors) throw entitlementResponse?.errors;

      const billingFrequency = get(entitlementResponse, 'data.entitlementDetails.billingFrequency');

      if (entitlementResponse && billingFrequency !== BillingFrequency.MONTHLY) {
        setEntitlementValidationError('Apps must be linked to a monthly product subscription.');
        return;
      }

      const relatesFromEntitlement = defaultTo(
        get(entitlementResponse, 'data.entitlementDetails.relatesFromEntitlements'),
        []
      );

      const productId = get(entitlementResponse, 'data.entitlementDetails.productId');
      const isItAJiraProduct = isJiraProduct(productId);

      if (isItAJiraProduct && !relatesFromEntitlement?.length) throw new Error('Family container details is missing.');

      const parentProductId = isItAJiraProduct
        ? JIRA_RELATIONSHIP_CONTAINER_ID
        : entitlementResponse?.data?.entitlementDetails?.productId;

      if (parentProductId) {
        const parentProductRelationship = await graphqlClient(ProxyGraphClients.SXP).query({
          query: checkAppCompatibilityWithParentProductQuery,
          variables: { parentProductKey: parentProductId, appProductKey: appProductKey },
          fetchPolicy: 'no-cache',
        });

        if (parentProductRelationship?.errors) throw parentProductRelationship?.errors;

        const parentProductDetails = parentProductRelationship?.data?.isAppCompatibleWithParentProduct;
        if (parentProductDetails?.isAppCompatibleWithProduct) {
          setIsNotParentProductCompatible(false);
          setEnabledEntitlementChangeButton(true);
          setEntitlementDetails(entitlementResponse?.data?.entitlementDetails as ParentEntitlementDetails);
          isItAJiraProduct && setIsReadyToFetchMaxUsersDetail(true);
          return;
        }
      }
      setIsNotParentProductCompatible(true);
      setEnabledEntitlementChangeButton(true);
      setEntitlementDetails(entitlementResponse?.data?.entitlementDetails as ParentEntitlementDetails);
    } catch (error) {
      setError(error as Error);
      notifyFailure(
        (error as Error)?.message ?? "This app isn't compatible with the selected product - try selecting another app.",
        CheckoutOperation.LinkAppToParentProduct
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    data: entitlementDetails,
    loading,
    error,
    entitlementValidationError,
    isMaxUsersDetailLoading,
    searchEntitlement,
    isNotParentProductCompatible,
    setIsNotParentProductCompatible,
    setEnabledEntitlementChangeButton,
    isEnabledEntitlementChangeButton,
    setEntitlementDetails,
    setIsReadyToFetchMaxUsersDetail,
  };
};
